import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { cremacaoPage, siteInfo } from '../data/mockData';

const Cremacao = () => {
  const [openFaq, setOpenFaq] = useState(null);

  // Atualizar SEO
  React.useEffect(() => {
    document.title = 'Cremação no Rio de Janeiro | Preços e Informações | 24h';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = 'Cremação no RJ com atendimento 24 horas. Consulte preços, documentação e procedimentos. Crematórios certificados. A partir de R$ 2.490.';
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  // Schema.org JSON-LD para FAQs (SEO Rich Snippets)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": cremacaoPage.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Serviço de Cremação",
    "provider": {
      "@type": "FuneralHome",
      "name": "Santa Casa Serviço Funerário Imediato",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rua Exemplo, 123",
        "addressLocality": "Meier",
        "addressRegion": "RJ",
        "addressCountry": "BR"
      },
      "telephone": siteInfo.phone,
      "availableLanguage": "Portuguese"
    },
    "areaServed": {
      "@type": "City",
      "name": "Rio de Janeiro"
    },
    "availableChannel": {
      "@type": "ServiceChannel",
      "serviceUrl": window.location.href,
      "availableLanguage": "Portuguese"
    }
  };

  return (
    <div className="min-h-screen">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>

      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(26, 188, 156, 0.85), rgba(20, 160, 133, 0.85)), url('${cremacaoPage.hero.image}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="text-center px-4 max-w-4xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">{cremacaoPage.hero.title}</h1>
          <p className="text-base md:text-lg text-teal-50">{cremacaoPage.hero.subtitle}</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-100 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-lime-600 hover:text-teal-600 transition-colors">
              Home
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Cremação</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {cremacaoPage.content.map((section, index) => (
            <div key={index} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{section.title}</h2>
              <p className="text-gray-700 leading-relaxed text-base md:text-lg">{section.text}</p>
            </div>
          ))}

          {/* Benefícios */}
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-8 mb-12 mt-12">
            <h3 className="text-2xl font-semibold text-gray-900 mb-6">O que inclui nosso serviço de cremação</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cremacaoPage.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-lime-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-800">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQs Section */}
          <div className="mb-12 mt-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center">Perguntas Frequentes sobre Cremação</h2>
            <div className="space-y-4">
              {cremacaoPage.faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 bg-white hover:bg-gray-50 transition-colors text-left"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-teal-600 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="p-5 pt-0 bg-gray-50">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gray-900 text-white rounded-lg p-8 text-center">
            <h3 className="text-2xl font-semibold mb-4">Precisa de ajuda ou mais informações?</h3>
            <p className="text-gray-300 mb-6">
              Nossa equipe está disponível 24 horas para atendê-lo com respeito e dedicação.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${siteInfo.phone.replace(/\D/g, '')}`}
                className="inline-flex items-center justify-center space-x-2 bg-teal-500 hover:bg-lime-600 text-white px-8 py-3 rounded-md text-lg font-medium transition-all duration-300 hover:shadow-lg hover:shadow-teal-500/50"
              >
                <Phone className="w-5 h-5" />
                <span>Fale Conosco</span>
              </a>
              <Link
                to="/contato"
                className="inline-block bg-white text-gray-900 hover:bg-gray-100 px-6 py-3 rounded-xl text-base font-bold transition-all duration-300"
              >
                Formulário de Contato
              </Link>
            </div>
          </div>

          {/* Back button */}
          <div className="mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-lime-600 hover:text-teal-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para início
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serviços de Cremação no Meier - Rio de Janeiro
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              A <strong>Santa Casa Serviço Funerário Imediato</strong> é especializada em <strong>serviços de cremação no Meier</strong> e em toda a região do Rio de Janeiro. 
              Com mais de 18.000 famílias atendidas, oferecemos um serviço completo e humanizado para quem busca <strong>cremação no Meier</strong>.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Nossa <strong>funerária no Meier</strong> oferece atendimento 24 horas para serviços de <strong>cremação</strong>, 
              com toda a documentação necessária, coordenação de cerimônias e acompanhamento completo do processo em crematórios certificados.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Localizada no coração do <strong>Meier, Rio de Janeiro</strong>, a Santa Casa Serviço Funerário Imediato é referência em <strong>serviços funerários</strong>, 
              <strong>cremação</strong> e <strong>translado</strong>. Entre em contato conosco para mais informações sobre nossos serviços de cremação.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cremacao;
